import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Progress } from 'reactstrap';
import dayjs from 'dayjs';

import { dateInputFieldFormat } from '~libs/dayjs';
import { Appointment, UnallocatedAppointment, Worker } from '~weekly-planner/lib/common';
import { buildAssignChange, SimulateAssignParams } from '~weekly-planner/lib/simulate';

import { Button, ErrorResponse } from '~components';
import { MultiCheckbox } from '~components/form-fields/inputs';
import Modal from '~components/Modal';

import { getAll as getBulkAssignees } from '~weekly-planner/actions/appointments/bulkAssignees';
import { simulate } from '~weekly-planner/actions/simulate';

import { makeChange } from '~weekly-planner/reducers/unsaved';

import { selectDerivedDateRange, selectPlannerAPIOptions } from '~weekly-planner/selectors';
import { selectBulkAssigneesError } from '~weekly-planner/selectors/bulkAssignees';
import { selectMergedVisualisedData } from '~weekly-planner/selectors/simulate';

interface ComponentProps {
  isOpen?: boolean;
  setIsOpen: (toggle: boolean) => void;
  onConfirm?: () => void;
}

interface ProgressState {
  inProgress: boolean;
  status: string;
}

interface BulkAssignOption {
  id: string;
  name: string;
}

const ConfirmBulkAssign: React.FC<ComponentProps> = ({ isOpen, setIsOpen, onConfirm }) => {
  const dispatch = useAppDispatch();

  const weeklyPlannerData = useAppSelector(selectMergedVisualisedData);
  const { week_start, week_end } = useAppSelector(selectDerivedDateRange);
  const plannerAPIOptions = useAppSelector(selectPlannerAPIOptions);
  const error = useAppSelector(selectBulkAssigneesError);
  const [checkboxValues, setCheckboxValues] = useState<string[]>([]);
  const [progressState, setProgressState] = useState<ProgressState>({ inProgress: false, status: '' });

  const id = 'confirm-bulk-assign';
  const ignoreAvailability = 'ignore-availability';
  const ignoreQualified = 'ignore-qualified';
  const options: BulkAssignOption[] = [
    { id: ignoreAvailability, name: 'Ignore Availability Checks' },
    { id: ignoreQualified, name: 'Ignore Qualification Checks' },
  ];

  const bulkSimulate = (change: SimulateAssignParams, week_start: string) => {
    const { workers, data }: { workers: Worker[]; data: Appointment[] } = change;
    const appointments = Array.isArray(data) ? data : [data];

    const process = (data: Appointment[], worker: Worker | undefined = undefined) =>
      dispatch(
        simulate({ type: 'assign', week_start, workers: worker ? [worker] : [], data, options: plannerAPIOptions }),
      );

    // Process known workers separately
    workers.forEach((worker) => {
      const data = appointments.filter(({ user_id }) => Number(user_id) === Number(worker.id));
      process(data, worker);
    });

    // Process any unknown workers separately, API will automatically pull the missing worker data
    const unknownWorkers = [
      ...new Set(
        appointments
          .filter(({ user_id }) => user_id && !workers.map(({ id }) => Number(id)).includes(Number(user_id)))
          .map(({ user_id }) => user_id),
      ),
    ];
    unknownWorkers.forEach((id) => {
      const data = appointments.filter(({ user_id }) => Number(user_id) === Number(id));
      process(data);
    });
  };

  const onBulkAssign = (appointments: UnallocatedAppointment[]) => {
    const assignRows = appointments.map(({ preferred_user, ...rest }) => ({ ...rest, user_id: preferred_user?.id }));
    const assignChange = buildAssignChange(weeklyPlannerData, assignRows as Appointment[]);

    const changes = Array.isArray(assignChange.data) ? assignChange.data : [assignChange.data];
    changes.forEach((change) => {
      const { id, parent_repeat_id, user_id, date } = change;

      const originalAppointment = appointments.find((original) => {
        // For appointments with IDs, match on ID
        if (id && original.id) {
          return id === original.id;
        }

        // For recurring appointments (null ID), match on parent_repeat_id AND date
        if (id === null && parent_repeat_id && original.parent_repeat_id) {
          return original.parent_repeat_id === parent_repeat_id && original.date === date;
        }

        return false;
      });

      if (!originalAppointment) return;

      dispatch(
        makeChange({
          original: originalAppointment,
          change: { id, parent_repeat_id, date, user_id },
        }),
      );
    });

    bulkSimulate(assignChange, week_start ?? dayjs().format(dateInputFieldFormat));
  };

  const fetchData = async (): Promise<{ data: UnallocatedAppointment[]; success: boolean }> => {
    setProgressState({ inProgress: true, status: 'Fetching Workers' });

    const result = await dispatch(
      getBulkAssignees({
        ignoreAvailability: checkboxValues.includes(ignoreAvailability),
        ignoreQualified: checkboxValues.includes(ignoreQualified),
        week_start,
        week_end,
      }),
    );

    if (result.payload?.success) {
      setProgressState({ inProgress: false, status: '' });
      return { data: result.payload.data, success: true };
    }

    setProgressState({ inProgress: false, status: 'Failed to fetch workers' });
    return { data: [], success: false };
  };

  const confirm = async () => {
    setProgressState({ inProgress: true, status: 'Assigning Appointments' });
    const { data: appointments, success } = await fetchData();

    if (!success) {
      return;
    }

    onBulkAssign(appointments);
    setProgressState({ inProgress: false, status: '' });
    onConfirm?.();
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      headerClassName="bg-success text-white"
      header="Bulk Assign Workers"
      footer={
        <>
          <Button
            size="sm"
            color="success"
            className="ps-4 pe-4 me-2"
            onClick={confirm}
            disabled={progressState.inProgress}
          >
            Confirm
          </Button>
          <Button size="sm" className="ps-4 pe-4" onClick={() => setIsOpen(false)}>
            Back
          </Button>
        </>
      }
    >
      <ErrorResponse message={error} />
      <div className="mb-4 pb-2 border-2 border-bottom">
        Please select below options for assigning workers <b>in bulk</b>
      </div>
      <MultiCheckbox
        type="multi-checkbox"
        id={id}
        name={id}
        value={checkboxValues}
        onChange={(_, value) => setCheckboxValues(value)}
        options={options}
        colSize={2}
      />
      {progressState.inProgress && (
        <Progress className="text-center" color="success" style={{ height: '25px' }} value={100} animated>
          {progressState.status}
        </Progress>
      )}
    </Modal>
  );
};

export default ConfirmBulkAssign;
